import { useEffect } from 'react';
import { Icon, type IconName } from '../ui';

export type Tab =
  | 'dashboard'
  | 'projection'
  | 'scenarios'
  | 'investments'
  | 'cohorts'
  | 'update'
  | 'history'
  | 'accounts'
  | 'assumptions'
  | 'settings'
  | 'privacy'
  | 'glossary';

interface Props {
  tab: Tab;
  onChange: (t: Tab) => void;
}

interface Item {
  id: Tab;
  label: string;
  icon: IconName;
  primary?: boolean;
}

const MOBILE_ITEMS: Item[] = [
  { id: 'dashboard', label: 'Home', icon: 'dashboard' },
  { id: 'projection', label: 'Plan', icon: 'chart' },
  { id: 'update', label: 'Update', icon: 'plus', primary: true },
  { id: 'investments', label: 'Invest', icon: 'investments' },
  { id: 'settings', label: 'More', icon: 'menu' },
];

const RAIL_ITEMS: Item[] = [
  { id: 'dashboard', label: 'Home', icon: 'dashboard' },
  { id: 'projection', label: 'Plan', icon: 'chart' },
  { id: 'scenarios', label: 'Plans', icon: 'scenarios' },
  { id: 'investments', label: 'Invest', icon: 'investments' },
  { id: 'cohorts', label: 'Cohorts', icon: 'cohorts' },
  { id: 'update', label: 'Update', icon: 'plus' },
  { id: 'history', label: 'History', icon: 'clock' },
  { id: 'settings', label: 'More', icon: 'menu' },
];

export function BottomNav({ tab, onChange }: Props) {
  useEffect(() => {
    if (window.location.hash !== `#${tab}`) window.location.hash = tab;
  }, [tab]);

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 inset-x-0 z-40 border-t border-ink-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:bg-ink-900/95 dark:border-ink-700 lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="mx-auto flex max-w-xl items-stretch justify-between px-2">
        {MOBILE_ITEMS.map(it => {
          const active = tab === it.id;
          return (
            <li key={it.id} className="flex-1">
              <button
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => onChange(it.id)}
                className={`group flex w-full flex-col items-center gap-0.5 px-1 py-2.5 text-[11px] font-medium transition ${
                  active
                    ? 'text-accent-600 dark:text-accent-500'
                    : 'text-ink-500 dark:text-ink-300'
                }`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl transition ${
                    it.primary
                      ? 'bg-accent-600 text-white shadow-card group-active:scale-95'
                      : active
                        ? 'bg-accent-50 dark:bg-ink-700'
                        : 'bg-transparent'
                  }`}
                >
                  <Icon name={it.icon} size={it.primary ? 24 : 20} />
                </span>
                <span className={it.primary ? 'mt-0.5' : ''}>{it.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function SideRail({ tab, onChange }: Props) {
  useEffect(() => {
    if (window.location.hash !== `#${tab}`) window.location.hash = tab;
  }, [tab]);

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-y-0 left-0 z-40 hidden w-16 flex-col items-center border-r border-ink-200 bg-white dark:bg-ink-900 dark:border-ink-800 lg:flex"
      style={{
        paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
        paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
      }}
    >
      <a
        href="#dashboard"
        onClick={e => {
          e.preventDefault();
          onChange('dashboard');
        }}
        className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-accent-600 text-sm font-black text-white"
        title="Cairn"
      >
        C
      </a>
      <ul className="flex flex-1 flex-col items-center gap-1 py-2">
        {RAIL_ITEMS.map(it => {
          const active = tab === it.id;
          return (
            <li key={it.id}>
              <button
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => onChange(it.id)}
                className={`group relative flex h-12 w-12 flex-col items-center justify-center gap-0.5 rounded-xl text-[9px] font-semibold transition ${
                  active
                    ? 'bg-accent-50 text-accent-700 dark:bg-accent-700/20 dark:text-accent-500'
                    : 'text-ink-500 hover:bg-ink-100 hover:text-ink-700 dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-ink-100'
                }`}
                title={it.label}
              >
                {active && (
                  <span
                    className="pointer-events-none absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-accent-600 dark:bg-accent-500"
                    aria-hidden
                  />
                )}
                <Icon name={it.icon} size={20} />
                <span>{it.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
